// ============================================================
// IssueSecureAccessWizard — /olivium/secure-access/olivium-issue.
//
// A 9-step operator wizard that CONFIGURES and issues a secure invitation through
// the canonical Olivium proxy (POST /api/access/create → ss-invite-create). It sits
// ALONGSIDE the native sq_access_codes surface (CreateSecureAccess.tsx) — it does
// not fork it and does not write portal/session/consent tables from the browser.
//
// Honesty guarantees (covered by SSR tests):
//   - Real targets only; an honest empty state when none exist.
//   - Least-privilege permission default.
//   - Dry-run preview is always safe; the live issue fails closed to PROVIDER_REQUIRED
//     when the engine is unconfigured — never a local substitute invitation.
//   - Idempotent submit (container key) + double-click guard (disabled while issuing).
//   - The one-time code / link are shown ONLY when genuinely returned, exactly once,
//     and are NEVER written to browser storage.
// ============================================================

import { useState } from 'react'
import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import { OLIVIUM_ACCENT } from '../portal-factory/portalFactory'
import { SectionCard, StateBlock, chipBtn, primaryBtn, inputStyle, fieldLabel } from '../portal-factory/PortalKit'
import { SECURE_TARGET_TYPES } from './secureAccessData'
import type { AccessCodeTargetType } from '../../../../api/client'
import {
  WIZARD_STEPS, AUDIENCES, EXPERIENCES, PERMISSIONS, EXPIRATIONS, DELIVERIES, SECURITY_CONTROLS,
  DEFAULT_FORM, stepValid, firstInvalidStepIndex, targetTypeMeta,
  type WizardForm, type WizardStatus, type Permission, type SecurityKey,
} from './issueWizardModel'

export interface IssueWizardResult {
  live: boolean
  dryRun: boolean
  inviteId: string
  status: string
  accessCode?: string | null // one-time plaintext — shown once, never persisted
  message: string
}

export interface IssueWizardTarget { id: string; label: string; type: AccessCodeTargetType }

export interface IssueSecureAccessWizardProps {
  status?: WizardStatus
  error?: string | null
  authRequired?: boolean
  forbidden?: boolean
  result?: IssueWizardResult | null
  providerHint?: string | null
  /** Real, canonical targets. `undefined` → operator enters a target reference;
   *  `[]` → honest empty state; populated → choose a real target. Never invented. */
  targets?: IssueWizardTarget[]
  /** SSR/deep-link helpers. */
  initialStep?: number
  initialForm?: Partial<WizardForm>
  onHome?: () => void
  onBackCommandCenter?: () => void
  onOpenHub?: () => void
  onIssue?: (form: WizardForm) => void
}

const label = (t: string) => <span style={fieldLabel}>{t}</span>

export default function IssueSecureAccessWizard(props: IssueSecureAccessWizardProps) {
  const { status = 'DRAFT', error, authRequired, forbidden, result, providerHint, targets } = props
  const [stepIdx, setStepIdx] = useState(props.initialStep ?? 0)
  const [form, setForm] = useState<WizardForm>({ ...DEFAULT_FORM, ...props.initialForm })
  const step = WIZARD_STEPS[stepIdx]
  const issuing = status === 'ISSUING'
  const set = <K extends keyof WizardForm>(k: K, v: WizardForm[K]) => setForm((f) => ({ ...f, [k]: v }))

  function togglePermission(p: Permission) {
    setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }))
  }
  function toggleSecurity(k: SecurityKey) {
    setForm((f) => ({ ...f, security: { ...f.security, [k]: !f.security[k] } }))
  }

  const canAdvance = stepValid(step.id, form)

  // ── auth / forbidden / result short-circuits ──
  if (authRequired) {
    return (
      <Frame {...props} accent={OLIVIUM_ACCENT} crumb="ISSUE">
        <StateBlock kind="auth" title="Sign in required" body="Your operator session has expired. Sign in to issue secure access." />
      </Frame>
    )
  }
  if (forbidden) {
    return (
      <Frame {...props} accent={OLIVIUM_ACCENT} crumb="ISSUE">
        <StateBlock kind="error" title="Not permitted" body="Issuing Olivium secure access needs an operator role. This account can’t issue here." />
      </Frame>
    )
  }
  if (result) {
    return (
      <Frame {...props} accent="#059669" crumb="ISSUED" title="Secure invitation issued">
        <SectionCard title={result.dryRun ? 'Dry run — nothing was issued' : 'Copy this now — it is shown once'} accent={result.dryRun ? OLIVIUM_ACCENT : '#b8862a'}>
          {result.dryRun ? (
            <p style={{ margin: 0, fontSize: 12, color: CC_INK.muted }}>{result.message}</p>
          ) : (
            <>
              {result.accessCode ? (
                <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', fontFamily: CC_MONO, fontSize: 15, fontWeight: 800, color: '#e0b050', letterSpacing: '0.04em', wordBreak: 'break-all' }}>{result.accessCode}</div>
              ) : null}
              <p style={{ margin: '10px 0 0', fontSize: 12, color: CC_INK.muted }}>{result.message}</p>
              <p style={{ margin: '6px 0 0', fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>Invite {result.inviteId} · {result.status} · code is never stored — copy it now.</p>
            </>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="button" style={primaryBtn} onClick={props.onOpenHub}>Done → Secure Access</button>
          </div>
        </SectionCard>
      </Frame>
    )
  }

  // ── provider-required (engine not wired) — honest, actionable ──
  const providerBlocked = status === 'PROVIDER_REQUIRED'

  return (
    <Frame {...props} accent={OLIVIUM_ACCENT} crumb="ISSUE" title="Issue Olivium secure access">
      {/* Stepper */}
      <div role="list" aria-label="Wizard steps" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {WIZARD_STEPS.map((s, i) => (
          <span key={s.id} role="listitem" aria-current={i === stepIdx ? 'step' : undefined}
            style={{ fontSize: 10, fontFamily: CC_MONO, letterSpacing: '0.04em', padding: '4px 8px', borderRadius: 6,
              background: i === stepIdx ? 'rgba(124,58,237,0.16)' : 'transparent',
              color: i === stepIdx ? OLIVIUM_ACCENT : i < stepIdx ? CC_INK.muted : CC_INK.faint,
              border: `1px solid ${i === stepIdx ? 'rgba(124,58,237,0.4)' : 'transparent'}` }}>
            {i + 1}. {s.title}
          </span>
        ))}
      </div>

      <SectionCard title={`Step ${stepIdx + 1} of ${WIZARD_STEPS.length} · ${step.title}`} accent={OLIVIUM_ACCENT}>
        {step.id === 'recipient' ? (
          <div style={col}>
            {label('Recipient name')}
            <input style={inputStyle} value={form.recipientName} onChange={(e) => set('recipientName', e.target.value)} placeholder="Full name" />
            {label('Email or verified identifier')}
            <input style={inputStyle} type="email" value={form.recipientEmail} onChange={(e) => set('recipientEmail', e.target.value)} placeholder="name@example.com" />
            {label('Organization (optional)')}
            <input style={inputStyle} value={form.organization} onChange={(e) => set('organization', e.target.value)} />
            {label('Relationship (audience)')}
            <Choice options={AUDIENCES} value={form.relationship} onPick={(v) => set('relationship', v)} />
            <Toggle checked={form.existingAccount} onToggle={() => set('existingAccount', !form.existingAccount)} text="This person already has an account" />
            <p style={hintText}>Audience is who they are — it stays separate from their permissions.</p>
          </div>
        ) : null}

        {step.id === 'experience' ? (
          <div style={col}>
            {label('What should they experience?')}
            <Choice options={EXPERIENCES} value={form.experience} onPick={(v) => set('experience', v)} />
          </div>
        ) : null}

        {step.id === 'target' ? (
          <div style={col}>
            {Array.isArray(targets) && targets.length === 0 ? (
              <StateBlock kind="empty" title="No targets available yet" body="Create a project, portal, or artifact first — this wizard only offers real targets, never placeholders." />
            ) : Array.isArray(targets) ? (
              <>
                {label('Choose a target')}
                <select style={inputStyle} value={form.targetId}
                  onChange={(e) => { const t = targets.find((x) => x.id === e.target.value); set('targetId', e.target.value); set('targetType', t?.type ?? ''); set('targetLabel', t?.label ?? '') }}>
                  <option value="">Select…</option>
                  {targets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </>
            ) : (
              <>
                {label('Target type')}
                <select style={inputStyle} value={form.targetType} onChange={(e) => set('targetType', e.target.value as AccessCodeTargetType | '')}>
                  <option value="">Unbound (no destination)</option>
                  {SECURE_TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                {form.targetType ? (
                  <>
                    {targetTypeMeta(form.targetType)?.note ? <p style={hintText}>{targetTypeMeta(form.targetType)!.note}</p> : null}
                    {label('Target id (required for a bound target)')}
                    <input style={inputStyle} value={form.targetId} onChange={(e) => set('targetId', e.target.value)} placeholder="Real id of the destination" />
                    {label('Target label (optional)')}
                    <input style={inputStyle} value={form.targetLabel} onChange={(e) => set('targetLabel', e.target.value)} />
                  </>
                ) : <p style={hintText}>An unbound code redeems but honestly reports “no destination”.</p>}
              </>
            )}
          </div>
        ) : null}

        {step.id === 'permissions' ? (
          <div style={col}>
            {label('Permissions (least privilege — start minimal)')}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PERMISSIONS.map((p) => (
                <button key={p} type="button" onClick={() => togglePermission(p)}
                  aria-pressed={form.permissions.includes(p)}
                  style={{ ...chipBtn, ...(form.permissions.includes(p) ? { borderColor: OLIVIUM_ACCENT, color: OLIVIUM_ACCENT } : {}) }}>{p}</button>
              ))}
            </div>
          </div>
        ) : null}

        {step.id === 'security' ? (
          <div style={col}>
            {label('Expiration')}
            <Choice options={EXPIRATIONS} value={form.expiration} onPick={(v) => set('expiration', v)} />
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>{label('Max uses')}<input style={inputStyle} inputMode="numeric" value={form.maxUses} onChange={(e) => set('maxUses', e.target.value)} /></div>
              <div style={{ flex: 1 }}>{label('Session minutes')}<input style={inputStyle} inputMode="numeric" value={form.sessionMinutes} onChange={(e) => set('sessionMinutes', e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {SECURITY_CONTROLS.map((c) => (
                <button key={c.key} type="button" onClick={() => toggleSecurity(c.key)} aria-pressed={form.security[c.key]}
                  style={{ ...chipBtn, ...(form.security[c.key] ? { borderColor: OLIVIUM_ACCENT, color: OLIVIUM_ACCENT } : {}) }}>{c.label}</button>
              ))}
            </div>
            <p style={hintText}>Download/print/copy restrictions are deterrents in the browser — not absolute DRM.</p>
          </div>
        ) : null}

        {step.id === 'nda' ? (
          <div style={col}>
            <Toggle checked={form.ndaRequired} onToggle={() => set('ndaRequired', !form.ndaRequired)} text="Require a confidentiality agreement (NDA)" />
            {form.ndaRequired ? (<>{label('NDA version')}<input style={inputStyle} value={form.ndaVersion} onChange={(e) => set('ndaVersion', e.target.value)} /></>) : null}
            <Toggle checked={form.monitoringDisclosure} onToggle={() => set('monitoringDisclosure', !form.monitoringDisclosure)} text="Show a monitoring disclosure" />
          </div>
        ) : null}

        {step.id === 'notifications' ? (
          <div style={col}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <li style={chRow}><span>In their workspace</span><em style={{ color: '#059669', fontStyle: 'normal', fontSize: 11 }}>Essential · always on</em></li>
              <li style={chRow}>
                <span id="sia-notify-email">Email</span>
                <Toggle checked={form.notifyEmail} onToggle={() => set('notifyEmail', !form.notifyEmail)} text="" ariaLabel="Email notifications" />
              </li>
              <li style={{ ...chRow, opacity: 0.55 }}><span>Browser / desktop push</span><em style={{ color: CC_INK.faint, fontStyle: 'normal', fontSize: 11 }}>Not available yet</em></li>
            </ul>
          </div>
        ) : null}

        {step.id === 'delivery' ? (
          <div style={col}>
            {label('How should this be delivered?')}
            <Choice options={DELIVERIES} value={form.delivery} onPick={(v) => set('delivery', v)} />
          </div>
        ) : null}

        {step.id === 'confirm' ? (
          <div style={col}>
            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 14px', fontSize: 12 }}>
              <Row k="Recipient" v={`${form.recipientName || '—'} · ${form.recipientEmail || '—'}`} />
              <Row k="Audience" v={form.relationship} />
              <Row k="Experience" v={form.experience} />
              <Row k="Target" v={form.targetType ? `${form.targetType}${form.targetId ? ` · ${form.targetId}` : ''}` : 'Unbound'} />
              <Row k="Permissions" v={form.permissions.join(', ')} />
              <Row k="Expiration" v={`${form.expiration} · ${form.maxUses} use(s)`} />
              <Row k="NDA" v={form.ndaRequired ? `Required (${form.ndaVersion})` : 'Not required'} />
              <Row k="Notifications" v={`Workspace${form.notifyEmail ? ' · Email' : ''}`} />
              <Row k="Delivery" v={form.delivery} />
            </dl>
            {providerBlocked ? (
              <div role="status" style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.34)', fontSize: 12, color: CC_INK.muted }}>
                <strong style={{ color: CC_INK.base }}>Provider required.</strong> {providerHint || 'The Olivium engine isn’t connected yet. A live invitation can’t be issued until an operator wires it. No local invitation was created.'}
              </div>
            ) : null}
            {error ? <p style={{ margin: 0, fontSize: 12, color: '#e0857a' }}>{error}</p> : null}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: CC_INK.muted }}>
              <input type="checkbox" checked={form.live} onChange={() => set('live', !form.live)} />
              Issue live (unchecked = safe dry-run preview)
            </label>
          </div>
        ) : null}
      </SectionCard>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
        <button type="button" style={chipBtn} disabled={stepIdx === 0 || issuing} onClick={() => setStepIdx((i) => Math.max(0, i - 1))}>← Back</button>
        {step.id !== 'confirm' ? (
          <button type="button" style={{ ...primaryBtn, opacity: canAdvance ? 1 : 0.5 }} disabled={!canAdvance}
            onClick={() => { if (canAdvance) setStepIdx((i) => Math.min(WIZARD_STEPS.length - 1, i + 1)) }}>Continue →</button>
        ) : (
          <button type="button" style={{ ...primaryBtn, opacity: issuing ? 0.6 : 1 }} disabled={issuing || firstInvalidStepIndex(form) !== -1}
            onClick={() => { if (!issuing) props.onIssue?.(form) }}>
            {issuing ? 'Issuing…' : form.live ? 'Issue live access' : 'Preview (dry run)'}
          </button>
        )}
        {firstInvalidStepIndex(form) !== -1 && step.id === 'confirm' ? (
          <span style={hintText}>Complete the required fields in step {firstInvalidStepIndex(form) + 1} first.</span>
        ) : null}
      </div>
    </Frame>
  )
}

// ── small presentational helpers ──

const col = { display: 'flex', flexDirection: 'column', gap: 8 } as const
const hintText = { margin: '2px 0 0', fontSize: 11, color: CC_INK.faint } as const
const chRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 10, fontSize: 12 } as const

function Frame(props: IssueSecureAccessWizardProps & { accent: string; crumb: string; title?: string; children?: React.ReactNode }) {
  return (
    <CommandCenterShell
      onHome={props.onHome} onBackCommandCenter={props.onBackCommandCenter}
      parentBack={{ label: '← Secure Access', onClick: props.onOpenHub }}
      breadcrumb={['OLIVIUM', 'SECURE ACCESS', props.crumb]}
      title={props.title ?? 'Issue Olivium secure access'} titleAccent={props.accent}
      honestyBadge={{ label: 'OLIVIUM AUTHORITY', title: 'Issued through the Olivium Secure Share engine — Square Code is the control plane, never a second issuer.' }}
    >
      {props.children}
    </CommandCenterShell>
  )
}

function Choice<T extends string>({ options, value, onPick }: { options: readonly T[]; value: T; onPick: (v: T) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onPick(o)} aria-pressed={o === value}
          style={{ ...chipBtn, ...(o === value ? { borderColor: OLIVIUM_ACCENT, color: OLIVIUM_ACCENT } : {}) }}>{o}</button>
      ))}
    </div>
  )
}

function Toggle({ checked, onToggle, text, ariaLabel }: { checked: boolean; onToggle: () => void; text: string; ariaLabel?: string }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: CC_INK.muted, cursor: 'pointer' }}>
      {/* When there is no visible text inside the label, an aria-label names the
          control (e.g. the Email toggle whose visible "Email" label is a sibling). */}
      <input type="checkbox" checked={checked} onChange={onToggle} aria-label={!text && ariaLabel ? ariaLabel : undefined} />
      {text}
    </label>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (<><dt style={{ color: CC_INK.faint, fontFamily: CC_MONO, fontSize: 11 }}>{k}</dt><dd style={{ margin: 0, color: CC_INK.base }}>{v}</dd></>)
}
